'use client';

import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import dynamic from 'next/dynamic';
import {
  acceptTosLocalStorage,
  fetchIsAllowed,
  isTosLocalStorageAccepted,
} from '@/components/ComplianceProvider/utils';
import { useVerifiedAccount } from '@/onchain/hooks/useVerifiedAccount';
import { useVerifiedCountry } from '@/onchain/hooks/useVerifiedCountry';
import { COMPLIANCE_VERIFIED_COUNTRIES } from '@/components/ComplianceProvider/constants';

// Dynamically import modals to reduce initial bundle size (~15KB)
// These modals render conditionally, so lazy loading them improves performance
const OFACModal = dynamic(async () =>
  import('@/components/OFACModal').then((mod) => ({ default: mod.OFACModal })),
);
const VerificationsModal = dynamic(async () =>
  import('@/components/VerificationsModal').then((mod) => ({ default: mod.VerificationsModal })),
);
const TOSModal = dynamic(async () =>
  import('@/components/TOSModal').then((mod) => ({ default: mod.TOSModal })),
);

type Props = {
  children: ReactNode;
};

type ComplianceContextType = ReturnType<typeof useComplianceContext>;

const COINBASE_VERIFICATIONS_URLS = {
  84532: 'https://coinbase-dev.cbhq.net/onchain-verify',
  8453: 'https://coinbase.com/onchain-verify',
};

const ComplianceContext = createContext<object>({});

function useComplianceContext() {
  // Compliance is only necessary for connected accounts
  const { address, chainId, isConnected } = useAccount();

  // Compliance modals dismiss state
  // - Allow modals to be dismissed by the user
  // - Default to true to ensure modals are shown on first load
  // - showComplianceModals is required for all other show*Modal checks
  const [showComplianceModals, setShowComplianceModals] = useState(true);
  const closeComplianceModals = useCallback(() => setShowComplianceModals(false), []);
  const openComplianceModals = useCallback(() => setShowComplianceModals(true), []);

  // OFAC - Request OFAC status
  const { data: isOFACAllowedData, isLoading: isOFACAllowedLoading } = useQuery({
    queryKey: ['isOFACBlocked', address],
    queryFn: async () => fetchIsAllowed(address),
    select: (r) => r.result,
  });
  // OFAC - Is loading when connected and OFAC status is loading
  const isLoadingOFACAllowed = isConnected && isOFACAllowedLoading;
  // OFAC - Is OFAC allowed when connected, OFAC is not loading and OFAC is allowed
  const isOFACAllowed = !!isOFACAllowedData && !isLoadingOFACAllowed;

  // Verifications - Request account and country verifications
  const {
    verified: isVerifiedAccount,
    isRetail: isRetailAccount,
    isLoading: isVerifyingAccount,
  } = useVerifiedAccount({
    address: address as `0x${string}`,
    chainId: chainId ?? 0,
  });
  const {
    attestationCountry,
    verified: isVerifiedCountry,
    isLoading: isVerifyingCountry,
  } = useVerifiedCountry({
    address: address as `0x${string}`,
    chainId: chainId ?? 0,
    verifiedCountries: COMPLIANCE_VERIFIED_COUNTRIES,
  });
  // Verifications - Is loading when connected and account or country is verifying
  const isLoadingVerifications = isConnected && (isVerifyingAccount || isVerifyingCountry);
  // Verifications - Is verified when connected and account and country are verified and is not loading
  const isVerified = isVerifiedAccount && isVerifiedCountry && !isLoadingVerifications;

  // TOS - Local storage key [TODO: Potentially persist this in the user's account]
  const [tosAccepted, setTosAccepted] = useState(() => isTosLocalStorageAccepted(address));
  // TOS - Initialize TOS accepted state from local storage when address has been loaded
  useEffect(() => {
    setTosAccepted(isTosLocalStorageAccepted(address));
  }, [address]);
  const handleAcceptTos = useCallback(() => {
    setTosAccepted(true);
    acceptTosLocalStorage(address);
  }, [address]);
  // TOS - Is accepted when connected and TOS is accepted
  const isTosAccepted = isConnected && tosAccepted;

  // Compliance checks are in flight
  const isLoadingCompliance = isLoadingOFACAllowed || isLoadingVerifications;
  // All compliance checks verified
  const isCompliant = !isLoadingCompliance && isOFACAllowed && isVerified && isTosAccepted;

  const { showOFACModal, showVerificationsModal, showTosModal } = useMemo(() => {
    // Initialize modals states
    const showModalsStates = {
      showOFACModal: false,
      showVerificationsModal: false,
      showTosModal: false,
    };
    // Only show modals when:
    // - Wallet is connected
    // - Compliance modals state is true
    // - Compliance checks are not in flight
    if (isConnected && showComplianceModals && !isLoadingCompliance) {
      if (!isOFACAllowed) {
        return { ...showModalsStates, showOFACModal: true };
      }
      if (!isVerified) {
        return { ...showModalsStates, showVerificationsModal: true };
      }
      if (!isTosAccepted) {
        return { ...showModalsStates, showTosModal: true };
      }
    }
    return showModalsStates;
  }, [
    isConnected,
    showComplianceModals,
    isLoadingCompliance,
    isOFACAllowed,
    isVerified,
    isTosAccepted,
  ]);

  // Verifications - Coinbase verification URL based on chainId
  const verificationUrl = useMemo(
    () => COINBASE_VERIFICATIONS_URLS[chainId as keyof typeof COINBASE_VERIFICATIONS_URLS],
    [chainId],
  );

  return {
    address,
    attestationCountry,
    chainId,
    isConnected,
    isLoadingCompliance,
    showOFACModal,
    showVerificationsModal,
    showTosModal,
    isOFACAllowed,
    isVerified,
    isVerifiedAccount,
    isVerifiedCountry,
    isTosAccepted,
    isCompliant,
    isRetailAccount,
    handleAcceptTos,
    closeComplianceModals,
    openComplianceModals,
    verificationUrl,
  };
}

export const ComplianceProvider = memo(function ComplianceProvider({ children }: Props) {
  const contextValue = useComplianceContext();

  return (
    <ComplianceContext.Provider value={contextValue}>
      <OFACModal />
      <VerificationsModal />
      <TOSModal />
      {children}
    </ComplianceContext.Provider>
  );
});

export function useCompliance() {
  return useContext(ComplianceContext) as ComplianceContextType;
}
